import { Button } from '@otter-ai/den-components';
import { isDesktop } from '@otter-ai/desktop';
import DOMPurify from 'isomorphic-dompurify';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { execWithWindow } from '@/features/left-nav/Misc/window';
import { FormattedText } from '@/shared/components/FormattedText';
import { AppRowLayout } from '../components/AppRowLayout';
import {
  getAppUiDetails,
  workatoAppUiDetails,
} from '../components/getAppUiDetails';
import { useWorkatoAppSettingsModal } from '../hooks/useWorkatoAppSettingsModal';
import { trackWorkato } from '../tracking/workato-tracking';
import type { AppRow } from '../type/AppRow';
import type { AppUiDetails } from '../type/AppUiDetails';
import type { WorkatoApp, WorkatoAppRawItem } from '../type/workato';

export function workatoAppRows(
  apps: WorkatoApp[],
  config: WorkatoAppRawItem[],
  t: ReturnType<typeof useTranslations>,
): AppRow[] {
  return useMemo(() => {
    const configUi = workatoAppUiDetails(config, t);
    return apps.map((app) => {
      const appUi = getAppUiDetails(app, configUi, t);
      return workatoToRow(app, appUi);
    });
  }, [apps, config]);
}

function workatoToRow(app: WorkatoApp, appUi: AppUiDetails): AppRow {
  const cleanDescription = DOMPurify.sanitize(appUi.description);
  const description = (
    <FormattedText>
      <div dangerouslySetInnerHTML={{ __html: cleanDescription }} />
    </FormattedText>
  );
  return {
    key: `workato-${app.id}`,
    meta: app,
    name: appUi.name,
    AppComponent: () => {
      const t = useTranslations('settings:integrations');
      const { openWorkatoAppSettingsModal } = useWorkatoAppSettingsModal();

      return (
        <AppRowLayout
          appRowId={`workato-${app.provider}`}
          iconUrl={appUi.icon}
          title={appUi.name}
          description={description}
          buttons={
            app.authorization_status === 'success' ? (
              <Button
                variant="outlineNeutral"
                version="legacy"
                onClick={() => openWorkatoAppSettingsModal(app)}
              >
                {t('buttons.disconnect_button')}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  trackWorkato.main.connectWorkato(app.name);
                  if (isDesktop()) {
                    execWithWindow((win) => {
                      const url = new URL(
                        '/setting/connected_apps',
                        win.location.origin,
                      );
                      url.searchParams.set(
                        'open_workato_app_id',
                        String(app.id),
                      );
                      win.open(url.toString(), '_blank');
                    });
                    return;
                  }
                  openWorkatoAppSettingsModal(app);
                }}
              >
                {t('buttons.add_button')}
              </Button>
            )
          }
        />
      );
    },
  };
}
